import { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Box,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { useAdminOrdersContext } from "../AdminOrdersContext";
import { IAdminOrder } from "../types";
import { OrderModel } from "../../../models/OrderModel";
import {
  getCustomerDeliveryPriceErrorMessage,
  resolveCustomerDeliveryPrice,
} from "../../../utils/deliveryPrice";

interface ICreateInvoiceModalProps {
  order: IAdminOrder;
  isOpen: boolean;
  onClose: () => void;
}

export const CreateInvoiceModal = ({
  order,
  isOpen,
  onClose,
}: ICreateInvoiceModalProps) => {
  const { setIsSendingInvoice } = useAdminOrdersContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const email = order?.userId?.email || "";

  const [serverErrorMsg, setServerErrorMsg] = useState<string | null>(null);
  const [salesRep, setSalesRep] = useState<string>('');
  const [truckNumber, setTruckNumber] = useState<string>('');
  const parsedInvoiceAmount =
    order?.paymentFollowUp?.status === "balance_due"
      ? order.paymentFollowUp.remainingAmount
      : resolveCustomerDeliveryPrice(order?.userId);
  const displayAmount =
    parsedInvoiceAmount !== null ? parsedInvoiceAmount.toFixed(2) : "Unavailable";
  const isBalanceInvoice = order?.paymentFollowUp?.status === "balance_due";
  const unavailablePriceMessage =
    !isBalanceInvoice && parsedInvoiceAmount === null
      ? getCustomerDeliveryPriceErrorMessage(order?.userId)
      : null;
  const hasActiveTsheetsReplacement = Boolean(order?.tsheetsReplacement);

  useEffect(() => {
    if (order) {
      setTruckNumber(order.lastCreatedInvoiceData?.truckNo || "");
    }
  }, [order]);

  const handleSubmit = async () => {
    // Keep this defensive check even though the table disables the button: replacement
    // state can arrive while an already-open modal is waiting for admin input.
    if (hasActiveTsheetsReplacement) {
      setServerErrorMsg(
        "Invoice creation is unavailable while the QB Time replacement is in progress. Please try again after it completes."
      );
      return;
    }

    // Validate sales rep format (optional, but recommended)
    const salesRepRegex = /^\d[A-Z]$/; // e.g., 7V, 6M
    const truckNumberRegex = /^\d+$/;

    if (salesRep && !salesRepRegex.test(salesRep) && truckNumber && !truckNumberRegex.test(truckNumber)) {
      setServerErrorMsg('Sales Rep must be in format: [number][initial] (e.g., 7V)');
      return;
    }

    if (order.status !== 'confirmed') {
      setServerErrorMsg('Order must be confirmed before creating invoice');
      return;
    }

    if (parsedInvoiceAmount === null) {
      setServerErrorMsg(getCustomerDeliveryPriceErrorMessage(order?.userId));
      return;
    }

    try {
      setIsSendingInvoice(true);
      setIsLoading(true);
      await OrderModel.createInvoice({
        ...order,
        salesRep,
        truckNumber,
      });
      setIsLoading(false);
      onClose();
      window.location.reload();
    } catch (e: any) {
      console.log(e.response?.data || e.message);
      const responseData = e.response?.data;
      setServerErrorMsg(
        typeof responseData === "string"
          ? responseData
          : responseData?.message || e.message || "Unknown error occurred"
      );
      setTimeout(() => {
        onClose();
      }, 3000);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth={true}
      open={isOpen}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        {isBalanceInvoice ? "Create Remaining Balance Invoice" : "Create Invoice"}
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          justifyContent="space-around"
          flexDirection="column"
        >
          <Box width="14rem">Customer email: {email || "N/A"}</Box>
          {isBalanceInvoice && (
            <Alert severity="warning" style={{ marginTop: "0.5rem" }}>
              Check payment received: ${Number(order.paymentFollowUp?.receivedAmount || 0).toFixed(2)}.
              Remaining balance to invoice: ${Number(order.paymentFollowUp?.remainingAmount || 0).toFixed(2)}.
            </Alert>
          )}
          {hasActiveTsheetsReplacement && (
            <Alert severity="warning" style={{ marginTop: "0.5rem" }}>
              Invoice creation is unavailable while the QB Time replacement is in progress.
              Please try again after it completes.
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="price"
            label="Total"
            type="text"
            value={displayAmount}
            fullWidth
            required
            InputProps={{
              readOnly: true,
            }}
            helperText={
              isBalanceInvoice
                ? "Read-only. Amount is the remaining balance after the partial check payment."
                : unavailablePriceMessage ||
                  "Read-only. Amount is derived from the customer profile."
            }
          />
          <TextField
            margin="dense"
            id="truckNumber"
            label="Truck Number Fallback"
            type="text"
            value={truckNumber}
            onChange={(e) => setTruckNumber(e.target.value)}
            fullWidth
            placeholder="Optional fallback truck number"
            // The server now resolves truck number from the driver's Full Shift first,
            // so this field only exists for incomplete or missing schedule data.
            helperText="Optional. Used only if the driver's Full Shift truck number cannot be resolved."
          />
          <TextField
            margin="dense"
            id="salesRep"
            label="(Optional) Sales Rep (e.g., 7V)"
            type="text"
            value={salesRep}
            onChange={(e) => setSalesRep(e.target.value)}
            fullWidth
            placeholder="Enter truck number and driver initial"
            helperText="Format: [truck number][driver initial] (e.g., 7V)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            isLoading ||
            parsedInvoiceAmount === null ||
            hasActiveTsheetsReplacement
          }
        >
          Send it!
        </Button>
      </DialogActions>
      {serverErrorMsg && <Alert severity="error">{serverErrorMsg}</Alert>}
    </Dialog>
  );
};
